export interface AboutSectionItem {
	label: string
	title?: string
	content?: string
	link?: string
	slug?: string
}

/** Nav-only destination for the Authors entry (it is not an about record). */
export const AUTHORS_LINK = '/authors'

/**
 * About records the old site served at their own URL (`/about/<slug>`).
 * Production rewrites them in `public/_redirects`; the router mirrors it for
 * client-side navigation and local dev (see `router.ts`). Keep the two in sync.
 */
export const LEGACY_ABOUT_SLUGS = [
	'about-us',
	'staff',
	'supporters',
	'our-values',
	'endorsements',
	'testimonials',
	'submissions',
]

function isAuthorsSection(section: AboutSectionItem): boolean {
	const link = (section.link || '').replace(/\/+$/, '')
	return link === AUTHORS_LINK
		|| (section.slug || '').trim().toLowerCase() === 'authors'
		|| (section.label || '').trim().toLowerCase() === 'authors'
}

/**
 * Side-nav sections for the About page.
 *
 * Stored `about-pages` records win over the authored schema defaults. The
 * Authors entry is a plain nav link rather than a page, so it is appended before
 * Submissions when no Authors record exists — and the CMS may already provide
 * one (a stored record, or the schema default used whenever the collection
 * resolves empty during prerender). Keep a single copy and never insert a
 * second one, or the menu renders "Authors" twice.
 */
export function buildAboutSections(
	stored: AboutSectionItem[],
	authored: AboutSectionItem[],
): AboutSectionItem[] {
	const source = (stored.length ? stored : authored).filter(
		(section, index, all) => !isAuthorsSection(section) || all.findIndex(isAuthorsSection) === index,
	)
	if (source.some(isAuthorsSection)) return source

	const authors: AboutSectionItem = { label: 'Authors', link: AUTHORS_LINK }
	const submissionsAt = source.findIndex(
		section => section.slug === 'submissions' || section.label === 'Submissions',
	)
	const insertAt = submissionsAt >= 0 ? submissionsAt : source.length
	return [...source.slice(0, insertAt), authors, ...source.slice(insertAt)]
}
